export const PLAYLIST_DRAFT_KEY = 'turbosync:playlist-draft:v1';
export const PLAYLIST_DRAFT_VERSION = 1;
export const PLAYLIST_MAX_ITEMS = 50;
export const PLAYLIST_NAME_MAX = 120;

export const IDLE_DELIVERY = Object.freeze({
  status: 'idle',
  itemIndex: 0,
  itemTotal: 0,
  errorMessage: null,
  correlationId: null,
  proFilePath: null,
  groupCount: null,
  // Lane 06 (spec §3.2): the run's station, the item being sent and the ones
  // still to send, the failure kind and the station's missing items.
  agentId: null,
  currentKey: null,
  pendingKeys: [],
  failedKind: null,
  failedKey: null,
  missingItemKeys: [],
  errorDetail: null,
  progressStage: null,
  progressLabel: null,
  deliveredAt: null,
  edited: false,
  returnStatus: null,
});

export const newPlaylistId = () => crypto.randomUUID();

const pad2 = (n) => String(n).padStart(2, '0');

export const toIsoDate = (date) =>
  `${date.getFullYear()}-${pad2(date.getMonth() + 1)}-${pad2(date.getDate())}`;

export const parseIsoDate = (iso) => {
  const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(iso ?? '');
  if (!match) return null;
  const [, y, m, d] = match.map(Number);
  const date = new Date(y, m - 1, d);
  return date.getMonth() === m - 1 ? date : null;
};

// Today counts as "next Sunday" until 14:00, when the morning service is over.
export const nextSundayIso = (now = new Date()) => {
  const date = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  const day = date.getDay();
  const isSundayMorning = day === 0 && now.getHours() < 14;
  const offset = isSundayMorning ? 0 : (7 - day) % 7 || 7;
  date.setDate(date.getDate() + offset);
  return toIsoDate(date);
};

export const formatMonthDay = (iso) =>
  parseIsoDate(iso)?.toLocaleDateString('en-US', { month: 'short', day: 'numeric' }) ?? '';

export const formatWeekdayMonthDay = (date) =>
  date.toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' });

export const defaultPlaylistName = (serviceDate = nextSundayIso()) =>
  `Sunday Service — ${formatMonthDay(serviceDate)}`;

// Saved-playlist bookkeeping (M3). `savedUpdatedAt` is the server's
// `updated_at` for the version this draft matches; null means `id` is only a
// client id and a save creates a new row.
export const UNSAVED = Object.freeze({ savedUpdatedAt: null, savedStatus: null, lastDeliveredAt: null });

export const createEmptyPlaylist = (now = new Date()) => {
  const serviceDate = nextSundayIso(now);
  return {
    id: null,
    name: defaultPlaylistName(serviceDate),
    serviceDate,
    targetAgentId: null,
    dirty: false,
    items: [],
    delivery: { ...IDLE_DELIVERY },
    ...UNSAVED,
  };
};

export const createPlaylistItem = (design) => ({
  itemKey: newPlaylistId(),
  designId: design.design_id || design.id,
  title: design.title || 'Untitled Design',
  thumbnailUrl: design.thumbnail_url ?? null,
  pageCount: Number.isInteger(design.page_count) ? design.page_count : null,
});

export const movePlaylistItem = (items, from, to) => {
  const inRange = (i) => Number.isInteger(i) && i >= 0 && i < items.length;
  if (!inRange(from) || !inRange(to) || from === to) return items;
  const next = [...items];
  const [moved] = next.splice(from, 1);
  next.splice(to, 0, moved);
  return next;
};

export const countSlides = (items) =>
  items.reduce((sum, item) => sum + (Number.isInteger(item.pageCount) ? item.pageCount : 1), 0);

export const hasUnknownSlideCount = (items) => items.some((item) => !Number.isInteger(item.pageCount));

export const serializeDraft = (playlist, savedAt = Date.now()) =>
  JSON.stringify({
    version: PLAYLIST_DRAFT_VERSION,
    savedAt,
    playlist: {
      id: playlist.id,
      name: playlist.name,
      serviceDate: playlist.serviceDate,
      targetAgentId: playlist.targetAgentId,
      dirty: playlist.dirty,
      savedUpdatedAt: playlist.savedUpdatedAt ?? null,
      savedStatus: playlist.savedStatus ?? null,
      lastDeliveredAt: playlist.lastDeliveredAt ?? null,
      items: playlist.items.map(({ itemKey, designId, title, thumbnailUrl, pageCount }) => ({
        itemKey, designId, title, thumbnailUrl, pageCount,
      })),
    },
  });

const isNonEmptyString = (value) => typeof value === 'string' && value.length > 0;
const isNullableString = (value) => value === null || typeof value === 'string';

const isValidItem = (item) =>
  item !== null
  && typeof item === 'object'
  && isNonEmptyString(item.itemKey)
  && isNonEmptyString(item.designId)
  && typeof item.title === 'string'
  && isNullableString(item.thumbnailUrl ?? null)
  && (item.pageCount === null || item.pageCount === undefined || Number.isInteger(item.pageCount));

export const parseDraft = (raw) => {
  let data;
  try {
    data = JSON.parse(raw);
  } catch {
    return null;
  }
  if (data?.version !== PLAYLIST_DRAFT_VERSION) return null;
  const draft = data.playlist;
  if (!draft || typeof draft !== 'object') return null;
  if (!Array.isArray(draft.items) || draft.items.length > PLAYLIST_MAX_ITEMS) return null;
  if (!draft.items.every(isValidItem)) return null;
  if (new Set(draft.items.map((item) => item.itemKey)).size !== draft.items.length) return null;
  if (!isNullableString(draft.id ?? null) || !isNullableString(draft.targetAgentId ?? null)) return null;
  if (!isNonEmptyString(draft.name) || draft.name.length > PLAYLIST_NAME_MAX) return null;
  if (draft.serviceDate !== null && parseIsoDate(draft.serviceDate) === null) return null;
  const saved = ['savedUpdatedAt', 'savedStatus', 'lastDeliveredAt'].every((key) => isNullableString(draft[key] ?? null))
    ? { savedUpdatedAt: draft.savedUpdatedAt ?? null, savedStatus: draft.savedStatus ?? null, lastDeliveredAt: draft.lastDeliveredAt ?? null }
    : UNSAVED;

  return {
    savedAt: Number.isFinite(data.savedAt) ? data.savedAt : null,
    playlist: {
      id: draft.id ?? null,
      name: draft.name,
      serviceDate: draft.serviceDate,
      targetAgentId: draft.targetAgentId ?? null,
      dirty: draft.dirty === true,
      items: draft.items.map(({ itemKey, designId, title, thumbnailUrl, pageCount }) => ({
        itemKey,
        designId,
        title,
        thumbnailUrl: thumbnailUrl ?? null,
        pageCount: pageCount ?? null,
      })),
      delivery: { ...IDLE_DELIVERY },
      ...saved,
    },
  };
};

export const buildDeliverPlaylistPayload = (playlist, agentId, mode) => ({
  lrmx_request_mode: mode,
  agent_id: agentId,
  playlist_id: playlist.id,
  playlist_name: playlist.name,
  items: playlist.items.map(({ designId, title }) => ({ design_id: designId, design_title: title })),
});

const UUID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
const httpsUrlOrNull = (url) => (typeof url === 'string' && url.startsWith('https://') && url.length <= 2048 ? url : null);

// Contract D `save` body. Replace semantics, so every field is sent; `position`
// is the carousel order. `id` only once the server has issued it.
export const buildSavePlaylistPayload = (playlist, { status, lastDeliveredAt, targetAgentId = playlist.targetAgentId } = {}) => ({
  ...(playlist.savedUpdatedAt && playlist.id ? { id: playlist.id } : {}),
  name: playlist.name.trim(),
  service_date: playlist.serviceDate ?? null,
  target_agent_id: UUID_RE.test(targetAgentId ?? '') ? targetAgentId : null,
  ...(status ? { status } : {}),
  ...(lastDeliveredAt ? { last_delivered_at: lastDeliveredAt } : {}),
  items: playlist.items.map((item, position) => ({
    position,
    design_id: item.designId,
    title: item.title,
    thumbnail_url: httpsUrlOrNull(item.thumbnailUrl),
    page_count: Number.isInteger(item.pageCount) ? item.pageCount : null,
  })),
});

// A saved row (contract D `list`/`save`) as a fresh draft: new itemKeys, the
// items in `position` order, clean.
export const playlistFromSaved = (row) => ({
  id: row.id,
  name: row.name,
  serviceDate: parseIsoDate(row.service_date) ? row.service_date : null,
  targetAgentId: row.target_agent_id ?? null,
  dirty: false,
  items: [...(row.items || [])]
    .sort((a, b) => a.position - b.position)
    .map((item) => createPlaylistItem(item)),
  delivery: { ...IDLE_DELIVERY },
  savedUpdatedAt: row.updated_at ?? null,
  savedStatus: row.status ?? 'draft',
  lastDeliveredAt: row.last_delivered_at ?? null,
});

// Drawer order (spec S8): service date descending, then most recently updated.
export const sortSavedPlaylists = (rows) => [...rows].sort((a, b) =>
  (b.service_date ?? '').localeCompare(a.service_date ?? '')
  || (b.updated_at ?? '').localeCompare(a.updated_at ?? ''));
